/**
 * @fileoverview Desktop dialog for creating a user from the users page.
 * @module modals/UserCreationModal
 */

import { useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import styles from "../styles/Users.module.css";
import { useUserCreationForm } from "../components/userCreation/useUserCreationForm";
import {
  UserCreationAvatar,
  UserCreationContactFields,
  UserCreationIdentityFields,
} from "../components/userCreation/UserCreationFields";
import {
  CREATE_BUTTON_SPINNER_SIZE,
  MODAL_SELECT_LAYOUT,
  userTypeSelectStyles,
} from "../components/userCreation/userCreationFormModel";

const I18N_PREFIX = "components.userCreationModal";

const fieldClasses = {
  section: undefined,
  label: styles.modalLabel,
  input: styles.modalInput,
  inputContainer: styles.inputContainer,
  inputWrapper: styles.inputWrapper,
  inputIcon: styles.inputIcon,
  inputClearButton: styles.inputClearButton,
  divider: styles.divider,
};

/**
 * Opens the native dialog while `open` is true.
 * @param {Object} input
 */
const useCreationDialog = ({ open, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };
    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }
    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  return dialogRef;
};

/**
 * Dialog for creating a user. Company members can type an RFC.
 * Other accounts keep the company RFC already on the account.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onAddUser
 * @param {Object} [props.user]
 * @returns {JSX.Element}
 */
const UserCreationModal = ({ open, onClose, onAddUser, user }) => {
  const dialogRef = useCreationDialog({ open, onClose });
  const form = useUserCreationForm({
    user,
    active: open,
    onAddUser,
    onFinished: onClose,
  });

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalAvatarBoxContainer}>
          <div className={styles.modalAvatarBox}>
            <UserCreationAvatar
              form={form}
              alt={form.t(`${I18N_PREFIX}.avatarAlt`)}
              classes={{
                avatar: styles.modalAvatar,
                avatarImage: styles.avatarImage,
                avatarButtons: styles.modalAvatarButtons,
                editButton: styles.modalAvatarEditButton,
                deleteButton: styles.modalAvatarDeleteButton,
              }}
            />
          </div>
        </div>
        <div className={`${styles.modalContent} ${styles.userCreationModalCompact}`}>
          <UserCreationIdentityFields
            form={form}
            classes={fieldClasses}
            i18nPrefix={I18N_PREFIX}
            selectStyles={userTypeSelectStyles(form.userTypeLocked, MODAL_SELECT_LAYOUT)}
          />
          <UserCreationContactFields
            form={form}
            user={user}
            classes={fieldClasses}
            i18nPrefix={I18N_PREFIX}
          />
        </div>
        {form.createError && (
          <p className={styles.formError} role="alert">
            {form.createError}
          </p>
        )}
        <div className={styles.modalButtons}>
          <button onClick={onClose} className={styles.cancelButton}>
            {form.t(`${I18N_PREFIX}.cancel`)}
          </button>
          <button
            className={styles.createUserButton}
            onClick={form.handleCreateUser}
            disabled={form.loading || !form.canSubmit}
          >
            {form.loading ? (
              <CircularProgress size={CREATE_BUTTON_SPINNER_SIZE} />
            ) : (
              form.t(`${I18N_PREFIX}.createUser`)
            )}
          </button>
        </div>
      </dialog>
      {form.showSuccessMessage && (
        <div className={styles.successMessage}>
          {form.t(`${I18N_PREFIX}.successMessage`)}
        </div>
      )}
    </>
  );
};

export default UserCreationModal;
